import { FC, useMemo, useCallback, AnchorHTMLAttributes, MouseEventHandler } from 'react'
import { Link, LinkProps } from 'react-router-dom'
import { Anchor } from 'interfaces/navigation'
import { getAnchorUrl } from 'helpers/pageHelpers'
import { QAMarked } from 'interfaces/component'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { remote } from 'helpers/mobileAppHelpers'
import { HOMEPAGE_URL } from 'tangle-shared'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { Omit } from 'interfaces'
import { useShouldBlockPage } from 'hooks/subscriptionHooks'
import { openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'

export type NavigationLinkProps =
  & AnchorHTMLAttributes<Link>
  & Omit<LinkProps, 'to'>
  & QAMarked
  & {
    to: Anchor
    search?: string
    preventNavigation?: boolean
  }

export const NavigationLink: FC<NavigationLinkProps> = (props) => {
  const { onClick, preventNavigation: preventNavigationProp, qaMarker, search, to, children: linkChildren, ...restProps } = props
  const dispatch = useStoreDispatch()
  const isPopup = remote.isAppOpenedInPopup()

  const shouldBlockPage = useShouldBlockPage(to)
  const preventNavigation = preventNavigationProp || shouldBlockPage

  const toUrl = useMemo(() => {
    if (!to) {
      return null
    }

    return getAnchorUrl(to)
  }, [to])

  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (shouldBlockPage) {
      dispatch(openSubscriptionPageActionCreator())
    }

    if (onClick) {
      onClick(e)
    }

    if (!preventNavigation && !!to) {
      dispatch(activateAnchorActionCreator(to))
    }
  }, [onClick, to, preventNavigation, shouldBlockPage, dispatch])

  const LinkWrapper = useMemo<FC | null>(() => {
    const commonProps = {
      ...restProps,
      'data-qa-marker': qaMarker,
      'data-qa-type': 'link',
      onClick: handleClick,
    }

    if (preventNavigation) {
      return ({ children }) => (
        <a { ...commonProps }>{ children }</a>
      )
    }

    return ({ children }) => (
      <Link { ...commonProps } to={{ pathname: toUrl || undefined, search }}>
        { children }
      </Link>
    )
  }, [preventNavigation, search, toUrl, handleClick, restProps, qaMarker])


  if (IS_STATUS_APP && isPopup && toUrl === HOMEPAGE_URL || !LinkWrapper) {
    return (
      <span className={ props.className }>
        { props.children }
      </span>
    )
  }

  return (
    <LinkWrapper>{ linkChildren }</LinkWrapper>
  )
}
